import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { PURGE } from "redux-persist"
import { Location } from "./locationsSlice"
import { RootState } from "./store"
import {
  ArrowDownToLine,
  BookmarkCheck,
  Fuel,
  LucideIcon,
  MapPin,
  MapPinPlusInside,
  Milestone,
  PackageCheck,
  Scale,
  Ship,
  ShoppingCart,
  SquareParking,
} from "lucide-react"
import { shallowEqual } from "react-redux"
import type { TFunction } from "i18next"
import {
  AvoidType,
  CarRouteConfig,
  RouteAlertConfig,
} from "@/api/adHocCommands"

export type RouteStop = {
  id?: string
  by?: string
  location: Location
  msgId?: string
  driverInitials?: string
  addressUpdated?: boolean
  action?: string
  action_reference_number?: string
  avoid?: AvoidType[]
  route_handle?: string
  ref?: string
  remaining_distance?: number
  remaining_time?: number
  scheduled_arrival_time?: string
  scheduled_hour_min?: string
  scheduled_hour_max?: string
  time_fixed?: boolean
  pallet_count?: string
  weight_kg?: string
  estimated_arrival_time?: string
  estimated_arrival_time_with_tacho?: string
  /** GPS set it when the tacho ETA waits for the driver to finish a rest it
   * only assumes is the daily one (stopped 3h+ well before the duty-window
   * end): when it lets them drive again. */
  assumed_rest_until?: string
  arrived_at?: string
  proximity_status?: string
  timeliness_status?: string
  isApproximate?: boolean
  is_active?: boolean
  is_current_target?: boolean
  is_late?: boolean
  is_temporary?: boolean
  isPending?: boolean
  shouldAlert?: boolean
  isLate?: boolean
  alertMetadata?: string
  deliveryOrderTimestamp?: string
  /** The delivery order this stop belongs to, as published by GPS. Kept apart
   * from `deliveryOrderTimestamp`, which is sent back on every route upsert and
   * which GPS treats as a new order. */
  order_timestamp?: string
  deliveryOrderId?: string
  polyline?: string
  source_metadata?: Record<string, unknown>
  external_status?: string
  external_status_at?: string
  external_status_source?: string
}

/** A stop of the current delivery order the driver has already left or skipped. */
export type PastRouteStop = {
  id: string
  location: Location
  action?: string
  ref?: string
  status?: "left" | "skipped"
  arrived_at?: string
  order_timestamp?: string
}

export type Toll = {
  fare_id?: string
  country_code: string
  location?: {
    lat: number
    lng: number
  }
  price: number
  currency: string
}

export type Route2g = {
  jid: string
  stops: RouteStop[]
  pastStops?: PastRouteStop[]
  lastUpdatedBy?: string
  tolls?: Toll[]
  routeId?: string
}

export type Action = {
  label: string
  value: string
  icon: LucideIcon
}

export const ACTIONS: Action[] = [
  { label: "Pickup", value: "pickup", icon: ShoppingCart },
  { label: "Delivery", value: "delivery", icon: PackageCheck },
  { label: "Refuel", value: "refuel", icon: Fuel },
  { label: "Parking", value: "parking", icon: SquareParking },
  { label: "Ferry", value: "ferry", icon: Ship },
  { label: "Customs", value: "customs", icon: BookmarkCheck },
  { label: "Drop-off", value: "drop-off", icon: ArrowDownToLine },
  { label: "Scale", value: "scale", icon: Scale },
  { label: "Waypoint", value: "waypoint", icon: MapPinPlusInside },
  { label: "Passthrough", value: "passthrough", icon: Milestone },
  { label: "Other", value: "other", icon: MapPin },
]

// Translation keys for the `ACTIONS` labels. The labels in `ACTIONS` stay in
// English: screens outside the maps area still show them as they are.
const ACTION_LABEL_KEYS = {
  pickup: "maps.stopActions.pickup",
  delivery: "maps.stopActions.delivery",
  refuel: "maps.stopActions.refuel",
  parking: "maps.stopActions.parking",
  ferry: "maps.stopActions.ferry",
  customs: "maps.stopActions.customs",
  "drop-off": "maps.stopActions.dropOff",
  scale: "maps.stopActions.scale",
  waypoint: "maps.stopActions.waypoint",
  passthrough: "maps.stopActions.passthrough",
  other: "maps.stopActions.other",
} as const

/** Translated label of a stop action, or undefined for an unknown action. */
export const stopActionLabel = (
  t: TFunction,
  action: string | undefined,
): string | undefined => {
  if (
    !action ||
    !Object.prototype.hasOwnProperty.call(ACTION_LABEL_KEYS, action)
  )
    return undefined
  return t(ACTION_LABEL_KEYS[action as keyof typeof ACTION_LABEL_KEYS])
}

export type ActionType =
  | "pickup"
  | "delivery"
  | "refuel"
  | "parking"
  | "ferry"
  | "customs"
  | "drop-off"
  | "scale"
  | "waypoint"
  | "passthrough"
  | "other"

export const isStopActive = (stop: RouteStop): boolean => {
  /*
    NOTE this is slightly different definition than in back-end,
    where we don't consider arrived as active and don't include
    this point in route calculation any more
  */
  return (
    stop.proximity_status !== undefined &&
    ["arrived", "arriving", "far", "never_outside_proximity"].includes(
      stop.proximity_status,
    )
  )
}

export const isStopPending = (stop: RouteStop): boolean => {
  return (
    stop.proximity_status !== undefined &&
    ["arriving", "far"].includes(stop.proximity_status)
  )
}

export const isStopLate = (stop: RouteStop): boolean => {
  return stop.timeliness_status === "late"
}

export const shouldAlert = (stop: RouteStop): boolean => {
  return isStopPending(stop) && isStopLate(stop)
}

export const findCurrentTarget = (
  stops: RouteStop[] | undefined,
): RouteStop | undefined => {
  return stops?.find(
    (stop) =>
      stop.action !== "passthrough" &&
      (stop.proximity_status === "arrived" ||
        stop.proximity_status === "arriving" ||
        stop.proximity_status === "far"),
  )
}

/**
 * Whether two stop lists describe the same planned stops (id, position,
 * avoided area), as opposed to the same stops with fresh live figures
 * (remaining_time, remaining_distance, proximity_status, ...). Used here to
 * decide whether an incoming route keeps the previous one's tolls; shared
 * with `useLatestDeliveryOrder`, which needs the same distinction to avoid
 * re-asking for the driver's order on every location update.
 */
export const sameStops = (previous: RouteStop[], next: RouteStop[]) =>
  previous.length === next.length &&
  previous.every(
    (stop, index) =>
      stop.id === next[index].id &&
      stop.location.lat === next[index].location.lat &&
      stop.location.lon === next[index].location.lon &&
      JSON.stringify(stop.avoid) === JSON.stringify(next[index].avoid),
  )

type Route2gState = {
  routes: Route2g[]
  routeConfigs: Record<string, CarRouteConfig> // driver JID -> config
  routeAlertsConfigs: Record<string, RouteAlertConfig> // driver JID -> config
}

const initialState: Route2gState = {
  routes: [],
  routeConfigs: {},
  routeAlertsConfigs: {},
}

type UpsertRouteAction = {
  route: Route2g
}

type BatchUpsertRoutesAction = {
  routes: Array<Route2g | undefined>
}

type UpsertRouteTollsAction = {
  jid: string
  tolls: Toll[] | undefined
}

type UpdateRouteConfigAction = {
  jid: string
  config: CarRouteConfig
}

type UpdateRouteAlertConfigAction = {
  jid: string
  config: Partial<RouteAlertConfig>
}

type BatchUpdateRouteAlertConfigsAction = {
  configs: Array<{ jid: string; config: RouteAlertConfig }>
}

const routes = createSlice({
  name: "routes2g",
  initialState,
  reducers: {
    upsertRoute2g: (state, action: PayloadAction<UpsertRouteAction>) => {
      const route = action.payload.route
      const routeIndex = state.routes.findIndex((p) => p.jid === route.jid)

      if (routeIndex === -1) {
        state.routes.push(route)
        return
      }
      const previous = state.routes[routeIndex]
      state.routes[routeIndex] = sameStops(previous.stops, route.stops)
        ? { ...route, tolls: route.tolls ?? previous.tolls }
        : route
    },
    batchUpsertRoute2g: (
      state,
      action: PayloadAction<BatchUpsertRoutesAction>,
    ) => {
      for (const route of action.payload.routes) {
        if (!route) {
          continue
        }
        const routeIndex = state.routes.findIndex((p) => p.jid === route.jid)

        if (routeIndex === -1) {
          state.routes.push(route)
        } else {
          const tolls = state.routes[routeIndex].tolls
          state.routes[routeIndex] = route
          state.routes[routeIndex].tolls = tolls
        }
      }
    },

    upsertRoute2gToll: (
      state,
      action: PayloadAction<UpsertRouteTollsAction>,
    ) => {
      const routeIndex = state.routes.findIndex(
        (p) => p.jid === action.payload.jid,
      )

      if (routeIndex !== -1) {
        state.routes[routeIndex].tolls = action.payload.tolls
      }
    },
    updateRouteConfig: (
      state,
      action: PayloadAction<UpdateRouteConfigAction>,
    ) => {
      // Completely replace the config instead of merging to allow clearing values
      state.routeConfigs[action.payload.jid] = action.payload.config
    },
    clearRouteConfig: (state, action: PayloadAction<{ jid: string }>) => {
      delete state.routeConfigs[action.payload.jid]
    },
    updateRouteAlertConfig: (
      state,
      action: PayloadAction<UpdateRouteAlertConfigAction>,
    ) => {
      // Merge with existing config to preserve other fields
      state.routeAlertsConfigs[action.payload.jid] = {
        ...state.routeAlertsConfigs[action.payload.jid],
        ...action.payload.config,
      }
    },
    clearRouteAlertConfig: (state, action: PayloadAction<{ jid: string }>) => {
      delete state.routeAlertsConfigs[action.payload.jid]
    },
    batchUpdateRouteAlertConfigs: (
      state,
      action: PayloadAction<BatchUpdateRouteAlertConfigsAction>,
    ) => {
      for (const { jid, config } of action.payload.configs) {
        state.routeAlertsConfigs[jid] = config
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const route2gEq = (
  prev: Route2g | undefined,
  next: Route2g | undefined,
) => {
  if (!prev || !next) {
    return false
  }

  if (prev.jid !== next.jid) {
    return false
  }

  if (prev.stops.length !== next.stops.length) {
    return false
  }

  for (let i = 0; i < prev.stops.length; i++) {
    if (!shallowEqual(prev.stops[i], next.stops[i])) {
      return false
    }
  }

  return true
}

export const findRoute2g = (jid: string | undefined) =>
  createSelector([(state: RootState) => state.routes2g], (routes2g) =>
    routes2g.routes.find((route) => route.jid === jid),
  )

export const findRoute2gTolls = (jid: string | undefined) =>
  createSelector(
    [(state: RootState) => state.routes2g],
    (routes2g) => routes2g.routes.find((route) => route.jid === jid)?.tolls,
  )

export const findRouteConfig = (jid: string | undefined) =>
  createSelector([(state: RootState) => state.routes2g], (routes2g) =>
    jid ? routes2g.routeConfigs[jid] : undefined,
  )

export const selectIsCarConfigured = (jid: string | undefined) =>
  createSelector(
    (state: RootState) => state.routes2g.routeConfigs,
    (configs) => {
      if (!jid) return false
      const cfg = configs[jid]
      if (!cfg) return false
      const hasDim = (v: string | undefined) => !!v && v.length > 0
      return (
        hasDim(cfg.width) ||
        hasDim(cfg.height) ||
        hasDim(cfg.length) ||
        hasDim(cfg.weight)
      )
    },
  )

export const findRouteAlertConfig = (jid: string | undefined) =>
  createSelector([(state: RootState) => state.routes2g], (routes2g) =>
    jid ? routes2g.routeAlertsConfigs[jid] : undefined,
  )

export const {
  upsertRoute2g,
  batchUpsertRoute2g,
  upsertRoute2gToll,
  updateRouteConfig,
  clearRouteConfig,
  updateRouteAlertConfig,
  clearRouteAlertConfig,
  batchUpdateRouteAlertConfigs,
} = routes.actions

export default routes.reducer
